// src/pages/Visitors/parts/types.ts
//
// Shared types + mappers for the Visitor Log admin screens.
// Mirrors enrichedVisitorLog in adminControllers/visitor_log.go.

export type VisitorStatus = 'checked_in' | 'checked_out' | 'denied';

// Raw row exactly as the API returns it (GET /visitors/today | /history | /:id)
export interface ApiVisitorLog {
  id: string;
  visitor_name: string;
  visitor_type: string;
  contact_no: string;
  id_proof_type: string;
  purpose: string;
  purpose_details: string;
  meet_person_type: string;
  meet_person_name: string;
  visit_date: string;
  check_in_time: string;
  check_out_time: string | null;
  status: VisitorStatus;
  visitor_photo_url: string;
  checked_in_by_name: string;
  checked_out_by_name: string;
  created_at: string;
}

// camelCase shape used by the components
export interface VisitorRecord {
  id: string;
  visitorName: string;
  visitorType: string;
  contactNo: string;
  idProofType: string;
  purpose: string;
  purposeDetails: string;
  meetPersonType: string;
  meetPersonName: string;
  visitDate: string;
  checkInTime: string;
  checkOutTime: string | null;
  status: VisitorStatus;
  photoUrl: string;
  checkedInByName: string;
  checkedOutByName: string;
  createdAt: string;
}

export interface VisitorPagination {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

export type VisitorStatusCounts = Record<VisitorStatus, number>;

export const mapApiVisitor = (row: ApiVisitorLog): VisitorRecord => ({
  id: row.id,
  visitorName: row.visitor_name || '',
  visitorType: row.visitor_type || '',
  contactNo: row.contact_no || '',
  idProofType: row.id_proof_type || '',
  purpose: row.purpose || '',
  purposeDetails: row.purpose_details || '',
  meetPersonType: row.meet_person_type || '',
  meetPersonName: row.meet_person_name || '',
  visitDate: row.visit_date || '',
  checkInTime: row.check_in_time || '',
  checkOutTime: row.check_out_time || null,
  status: row.status,
  photoUrl: row.visitor_photo_url || '',
  checkedInByName: row.checked_in_by_name || '',
  checkedOutByName: row.checked_out_by_name || '',
  createdAt: row.created_at || '',
});

export const STATUS_META: Record<
  VisitorStatus,
  { label: string; color: string; bg: string; border: string; icon: string }
> = {
  checked_in: {
    label: 'On premises',
    color: '#15803d',
    bg: 'rgba(22,163,74,.10)',
    border: 'rgba(22,163,74,.25)',
    icon: 'pi pi-sign-in',
  },
  checked_out: {
    label: 'Checked out',
    color: '#475569',
    bg: 'rgba(100,116,139,.10)',
    border: 'rgba(100,116,139,.25)',
    icon: 'pi pi-sign-out',
  },
  denied: {
    label: 'Denied',
    color: '#b91c1c',
    bg: 'rgba(239,68,68,.10)',
    border: 'rgba(239,68,68,.25)',
    icon: 'pi pi-ban',
  },
};

export const VISITOR_TYPE_OPTIONS = [
  'Parent/Guardian',
  'Vendor',
  'Delivery',
  'Job Applicant',
  'Official',
  'Other',
];

export const ID_PROOF_OPTIONS = [
  'Aadhaar Card',
  'Driving License',
  'Voter ID',
  'Passport',
  'Employee ID',
  'Other',
];

export const PURPOSE_OPTIONS = [
  'Meet Teacher',
  'Meet Principal',
  'Admission Inquiry',
  'Delivery',
  'Maintenance',
  'Interview',
  'Fee Payment',
  'Other',
];

export const MEET_PERSON_TYPE_OPTIONS = [
  'Teacher',
  'Principal',
  'Admin Staff',
  'Accountant',
  'Other Staff',
];

export const getInitials = (name: string): string =>
  name
    ?.trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

// visit_date is a plain "YYYY-MM-DD" string, check_in/out_time are Go
// time.Time values serialised as ISO timestamps — they format differently.
export const formatVisitDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Local YYYY-MM-DD — avoids the UTC shift you get from toISOString() on
// dates picked in a timezone ahead of / behind UTC.
export const toDateParam = (d: Date | null): string => {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};